import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { getPatronByUserQuery } from '../../components/artifacts/util/patronIdUtils'
import { getArtifactGroups, getPatronArtifacts } from '../../services/artifactService'
import useNotifications from '../useNotifications'

/**
 * Hook for fetching search results for a patron's artifacts.
 * Uses same search engine as patron-facing Find page.
 * @param {userQuery: string, artifactCategory: string, paginatorOffset: number, paginatorCount: number, includeAssociatedArtifacts: boolean}
 * @returns {Object} object with loading boolean, error status/data, artifacts array, and page count
 */
const usePatronArtifacts = ({
  userQuery,
  artifactCategory,
  paginatorOffset,
  paginatorCount,
  includeAssociatedArtifacts = false,
  artifactStatus = 'default',
  patron,
  setIndex,
}) => {
  const { t } = useTranslation()
  const [shouldRefetch, setShouldRefetch] = useState(true)
  const [pageCount, setPageCount] = useState(0)
  const [artifacts, setArtifacts] = useState(null)
  const [totalArtifactsSpanningPages, setTotalArtifactsSpanningPages] = useState(0)
  const { error } = useNotifications()
  const refetch = () => setShouldRefetch(!shouldRefetch)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Translate userQuery to patronId
        const response = await getPatronByUserQuery(userQuery, false)
        const patronId = response.data.id
        if (patronId === null) {
          const message = t('user.error.notFound', 'User not found')
          throw message
        }

        const { status, data } = await getPatronArtifacts(
          paginatorCount,
          paginatorOffset,
          includeAssociatedArtifacts,
          artifactCategory,
          patronId,
          artifactStatus
        )
        if (status !== 200) {
          throw data || status
        }
        // Since artifacts may not be publicly accessible and we impersonated,
        // modify the image URL to include session ID
        // const newArtifacts = data.artifact.map((a) => sessionifyArtifactUrls(a))
        setArtifacts(data.artifact)
        setPageCount(Math.ceil(data.totalArtifactsSpanningPages / paginatorCount))
        setTotalArtifactsSpanningPages(data.totalArtifactsSpanningPages || data.artifact.length)
        setIndex &&
          setIndex((prev) =>
            // Make sure that the index is set between 0 and the page count
            Math.max(Math.min(Math.ceil(data.totalArtifactsSpanningPages / paginatorCount) - 1, prev), 0)
          )
        const artifactsWithGroups = await Promise.allSettled(
          data.artifact.map(async (artifact) => {
            if (artifact.visibility === 'PRIVATE') {
              await getArtifactGroups(artifact.id)
                .then((groupResponse) => {
                  artifact.groupIds = groupResponse.data.groupIds
                })
                .catch((err) => {
                  err.response?.status !== 403 &&
                    error(
                      t(
                        'artifacts.error.getArtifactGroups',
                        'Error getting artifact groups for artifact {artifactId}',
                        {
                          artifactId: artifact.id,
                        }
                      ),
                      err
                    )
                })
            }
            return artifact
          })
        )
        setArtifacts(artifactsWithGroups.map((a) => a.value))
      } catch (err) {
        error(t('hooks.errors.gettingPatronArtifacts', `Error getting patron artifacts`), err)
        setArtifacts([])
        setPageCount(1)
      }
    }
    if (patron) {
      fetchData()
    }
  }, [
    userQuery,
    artifactCategory,
    includeAssociatedArtifacts,
    paginatorCount,
    paginatorOffset,
    shouldRefetch,
    artifactStatus,
    error,
    t,
    patron,
    setIndex,
  ])

  return {
    artifacts,
    setArtifacts,
    totalArtifactsSpanningPages,
    refetch,
    pageCount,
  }
}

export default usePatronArtifacts
